import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FACILITY } from "@/data/content";

export const FacilitySlider = () => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => embla && embla.scrollTo(i), [embla]);
  const prev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const next = useCallback(() => embla && embla.scrollNext(), [embla]);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => embla.scrollNext(), 5000);
    return () => { clearInterval(id); embla.off("select", onSelect); };
  }, [embla]);

  return (
    <div data-testid="facility-slider" className="relative">
      <div className="overflow-hidden rounded-[2rem]" ref={emblaRef}>
        <div className="flex">
          {FACILITY.map((f, i) => (
            <div className="relative min-w-0 flex-[0_0_100%] md:flex-[0_0_82%] px-2 md:px-3" key={i}>
              <div className="relative h-[52vh] md:h-[68vh] overflow-hidden rounded-[2rem]">
                <img src={f.src} alt={f.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep/85 via-maroon-deep/10 to-transparent" />
                <AnimatePresence mode="wait">
                  {selected === i && (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
                      className="absolute bottom-8 left-8 right-8 md:bottom-12 md:left-12 md:right-12"
                    >
                      <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">0{i + 1} / 0{FACILITY.length}</p>
                      <h3 className="font-heading text-3xl md:text-5xl text-cream">{f.title}</h3>
                      <p className="mt-3 max-w-xl text-cream/80">{f.caption}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className="flex gap-2">
          {FACILITY.map((_, i) => (
            <button
              key={i}
              data-testid={`facility-dot-${i}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${selected === i ? "w-10 bg-sunrise" : "w-4 bg-stone-300 hover:bg-stone-400"}`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
        <div className="flex gap-3">
          <button data-testid="facility-prev" onClick={prev} className="h-12 w-12 rounded-full border border-stone-300 flex items-center justify-center text-maroon-deep hover:bg-maroon-deep hover:text-cream hover:border-maroon-deep transition-colors duration-300">
            <ChevronLeft size={20} />
          </button>
          <button data-testid="facility-next" onClick={next} className="h-12 w-12 rounded-full border border-stone-300 flex items-center justify-center text-maroon-deep hover:bg-maroon-deep hover:text-cream hover:border-maroon-deep transition-colors duration-300">
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
